import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PRODUCTIONS, REVIEWS, getStats, imageForProduction, TICKETS_URL } from "../data/reviews";
import Stars from "../components/Stars";
import SEO from "../components/SEO";
import MagneticButton from "../components/MagneticButton";

/**
 * Reviews archive page — filterable and grouped-by-production.
 * The header presents dramatic press stats; the archive itself is dense
 * editorial rows with a hover-reveal of the production still.
 */
export default function Reviews() {
  const stats = getStats();
  const [filter, setFilter] = useState("all");
  const [hoverImg, setHoverImg] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const filtered = useMemo(() => {
    if (filter === "all") return REVIEWS;
    if (filter === "rave") return REVIEWS.filter((r) => r.positivity === "Rave");
    if (filter === "5star") return REVIEWS.filter((r) => r.rating === 5);
    return REVIEWS.filter((r) => r.slug === filter);
  }, [filter]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!map[r.slug]) map[r.slug] = [];
      map[r.slug].push(r);
    });
    return Object.entries(map).sort(
      ([a], [b]) => PRODUCTIONS.findIndex((p) => p.slug === b) - PRODUCTIONS.findIndex((p) => p.slug === a),
    );
  }, [filtered]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AggregateRating",
      itemReviewed: {
        "@type": "TheaterGroup",
        name: "COLAB Theatre",
      },
      ratingValue: stats.average_rating,
      reviewCount: stats.total_reviews,
      bestRating: 5,
      worstRating: 1,
    },
    ...REVIEWS.slice(0, 40).map((r) => ({
      "@context": "https://schema.org",
      "@type": "Review",
      itemReviewed: {
        "@type": "TheaterEvent",
        name: r.show,
        performer: { "@type": "TheaterGroup", name: "COLAB Theatre" },
      },
      author: { "@type": "Person", name: r.reviewer },
      publisher: { "@type": "Organization", name: r.outlet },
      reviewBody: `${r.quote} — ${r.summary}`,
      url: r.url,
      datePublished: String(r.date),
      ...(r.rating !== null && {
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
      }),
    })),
  ];

  const filters = [
    { id: "all", label: "All reviews" },
    { id: "rave", label: "Raves only" },
    { id: "5star", label: "5★ press" },
    ...PRODUCTIONS.map((p) => ({ id: p.slug, label: p.title })),
  ];

  return (
    <main
      data-testid="reviews-page"
      className="min-h-screen bg-[#050505]"
      onMouseMove={(e) => setCursor({ x: e.clientX, y: e.clientY })}
    >
      <SEO
        title="Immersive Theatre Reviews — COLAB Theatre | Best Immersive Theatre in London"
        description="Every immersive theatre review of COLAB Theatre — the best immersive theatre in London. 65+ verified immersive theatre reviews from The Guardian, The Stage, Time Out, LondonTheatre1, BroadwayWorld and 30+ more. 14 years of immersive theatre reviews in one place."
        canonical="https://www.colabtheatre.co.uk/reviews"
        image="https://images.squarespace-cdn.com/content/v1/525bd8b5e4b0daa056d3ae62/1627040263929-3EYP2HSD7BQJ2XBFAWRE/crooks_webres-131.jpg"
        jsonLd={jsonLd}
      />

      {/* Stats hero */}
      <section className="pt-40 md:pt-56 pb-24 px-6 md:px-14 lg:px-20 xl:px-24 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#8b0000] mb-8">
          Immersive Theatre Reviews · Best Immersive Theatre London · 2014 → 2026
        </div>
        <h1 className="font-display uppercase leading-[0.9] tracking-tight text-[10vw] md:text-[8vw]">
          Immersive theatre reviews.
          <span className="block text-white/40">
            Best immersive theatre London.
          </span>
        </h1>

        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-x-8">
          {[
            { n: stats.total_reviews, l: "Immersive Theatre Reviews" },
            { n: stats.rave_count, l: "Rave Immersive Theatre Reviews" },
            { n: stats.five_star_count, l: "5★ Immersive Theatre Reviews" },
            { n: stats.outlet_count, l: "London Press Outlets" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-5xl md:text-7xl leading-none text-white">
                {s.n}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.3em] font-mono text-white/50">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center gap-4">
          <MagneticButton href={TICKETS_URL} variant="velvet" testid="reviews-book-cta">
            Book tickets →
          </MagneticButton>
          <MagneticButton href="/api/press-kit.pdf" variant="ghost" testid="reviews-press-kit-cta">
            Download press kit (PDF)
          </MagneticButton>
        </div>
      </section>

      {/* Filter rail */}
      <section
        className="sticky top-[88px] md:top-[100px] z-30 bg-[#050505]/95 backdrop-blur-md border-b border-white/10"
        data-testid="reviews-filters"
      >
        <div className="px-6 md:px-14 lg:px-20 xl:px-24 py-4 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {filters.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-mono border border-white/10 transition-colors ${
                  filter === f.id
                    ? "chip-active"
                    : "text-white/60 hover:text-white hover:border-white/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cursor image reveal */}
      <AnimatePresence>
        {hoverImg && (
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            src={hoverImg}
            alt="Best immersive theatre in London — COLAB Theatre immersive theatre production still"
            className="fixed pointer-events-none z-40 hidden md:block"
            style={{
              top: cursor.y,
              left: cursor.x,
              width: "320px",
              height: "400px",
              objectFit: "cover",
              transform: "translate(-50%, -50%)",
              filter: "grayscale(0.2) contrast(1.15)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Grouped reviews */}
      <section className="px-6 md:px-14 lg:px-20 xl:px-24 py-20 md:py-28" data-testid="reviews-list">
        {grouped.length === 0 && (
          <div className="text-white/60 py-20">
            No reviews match this filter.
          </div>
        )}
        {grouped.map(([slug, rows]) => {
          const prod = PRODUCTIONS.find((p) => p.slug === slug);
          if (!prod) return null;
          const img = imageForProduction(slug);
          return (
            <div
              key={slug}
              data-testid={`review-group-${slug}`}
              className="mb-20 md:mb-32 border-t border-white/15 pt-10 md:pt-14"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-10">
                <div className="md:col-span-2">
                  <div className="font-display text-3xl md:text-5xl leading-none">
                    {prod.year}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.3em] font-mono text-white/40">
                    Chapter {prod.chapter}
                  </div>
                </div>
                <div className="md:col-span-7">
                  <Link
                    to={`/productions/${prod.slug}`}
                    className="group inline-block"
                    data-testid={`reviews-group-link-${prod.slug}`}
                  >
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95] text-white group-hover:text-[#c9a961] transition-colors">
                      {prod.title}
                      <span className="inline-block ml-3 text-white/40 group-hover:text-[#c9a961] transition-colors">↗</span>
                    </h2>
                  </Link>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.3em] font-mono text-[#c9a961]">
                    {prod.genre} · {prod.venue}
                  </div>
                </div>
                <div className="md:col-span-3 flex md:justify-end items-start gap-6 text-[10px] uppercase tracking-[0.3em] font-mono text-white/50">
                  <span>{rows.length} reviews</span>
                  {rows.filter((r) => r.positivity === "Rave").length > 0 && (
                    <span>
                      {rows.filter((r) => r.positivity === "Rave").length} raves
                    </span>
                  )}
                  <a
                    href={prod.official_url}
                    target="_blank"
                    rel="noreferrer"
                    className="link-hover text-white/70 hover:text-white"
                    data-testid={`reviews-group-official-${prod.slug}`}
                  >
                    On COLAB ↗
                  </a>
                </div>
              </div>

              <ul className="border-t border-white/10">
                {rows.map((r, i) => (
                  <li
                    key={`${slug}-${i}`}
                    onMouseEnter={() => setHoverImg(img)}
                    onMouseLeave={() => setHoverImg(null)}
                    data-testid={`review-row-${slug}-${i}`}
                    className="group border-b border-white/10 hover:bg-white/[0.02] transition-colors"
                  >
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block px-1 md:px-4 py-6 md:py-8"
                    >
                      <div className="grid grid-cols-12 gap-4 md:gap-8 items-baseline">
                        <div className="col-span-4 md:col-span-3">
                          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/60">
                            {r.outlet}
                          </div>
                          <div className="mt-1 text-white/40 text-xs">
                            {r.reviewer}
                          </div>
                        </div>
                        <div className="col-span-8 md:col-span-7">
                          <div className="font-display text-xl md:text-3xl leading-tight text-white group-hover:text-[#c9a961] transition-colors">
                            “{r.quote}”
                          </div>
                          <div className="mt-3 text-white/50 text-sm max-w-3xl">
                            {r.summary}
                          </div>
                        </div>
                        <div className="col-span-12 md:col-span-2 flex md:justify-end items-center gap-4">
                          {r.rating !== null ? (
                            <Stars value={r.rating} />
                          ) : (
                            <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/40">
                              Editorial
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-white/30 group-hover:text-white transition-colors">
                            ↗
                          </span>
                        </div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>
    </main>
  );
}
